import React, { useEffect, useState, useCallback, useRef } from 'react';
import { SlidersHorizontal, Plus, Pencil, Trash2, Search, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, X } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useT } from '../../i18n/LanguageContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useAuth } from '../../context/AuthContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const STATUS_BADGE = {
  ok: 'bg-emerald-100 text-emerald-700',
  duplicate: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
};

function ClassMultiSelect({ classes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const label = selected.length === 0 ? 'All classes' : `${selected.length} selected`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal" data-testid="fee-type-classes-trigger">
          <span className={selected.length === 0 ? 'text-muted-foreground' : ''}>{label}</span>
          <SlidersHorizontal className="w-4 h-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Leave empty = applies to all classes</span>
          {selected.length > 0 && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onChange([])} data-testid="fee-type-classes-clear">Clear</Button>
          )}
        </div>
        <ScrollArea className="max-h-64">
          <div className="p-1">
            {classes.length === 0 ? (
              <p className="text-sm text-muted-foreground p-3">No classes in the selected academic year.</p>
            ) : classes.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/60 cursor-pointer" data-testid={`fee-type-class-opt-${c.id}`}>
                <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="text-sm">{c.name}</span>
              </label>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default function FeesTypesPage() {
  const orgId = useOrgId();
  const { t } = useT();
  const { selectedYearId } = useAcademicYear();
  const { checkActionPermission } = useAuth();
  const MOD = '/school/fees/types';
  const canAdd = checkActionPermission(MOD, 'add');
  const canEditTypes = checkActionPermission(MOD, 'edit');
  const canDeleteTypes = checkActionPermission(MOD, 'delete');
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ fee_group_id: '', name: '', due_date: '', description: '', default_amount: 0, applicable_class_ids: [] });
  const [saving, setSaving] = useState(false);

  // import state
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [ts, gs] = await Promise.all([schoolApi.listFeeTypes(orgId, { search }), schoolApi.listFeeGroups(orgId)]);
      setRows(ts); setGroups(gs);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [orgId, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (orgId) schoolApi.listClasses(orgId, selectedYearId).then((c) => setClasses(Array.isArray(c) ? c : [])).catch(() => setClasses([]));
  }, [orgId, selectedYearId]);

  const openNew = () => { setForm({ fee_group_id: groups[0]?.id || '', name: '', due_date: '', description: '', default_amount: 0, applicable_class_ids: [] }); setDialog({}); };
  const openEdit = (r) => { setForm({ fee_group_id: r.fee_group_id, name: r.name, due_date: r.due_date || '', description: r.description || '', default_amount: r.default_amount || 0, applicable_class_ids: r.applicable_class_ids || [] }); setDialog(r); };

  const save = async () => {
    if (!form.name.trim() || !form.fee_group_id) { toast.error('Name and group required'); return; }
    setSaving(true);
    try {
      const body = { organization_id: orgId, ...form, default_amount: Number(form.default_amount) || 0 };
      if (dialog?.id) await schoolApi.updateFeeType(dialog.id, body);
      else await schoolApi.createFeeType(body);
      toast.success('Saved'); setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const remove = async (r) => { if (!window.confirm(`Delete "${r.name}"?`)) return; try { await schoolApi.deleteFeeType(r.id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  // ---- Import handlers ----
  const openImport = () => { setPreview(null); setSummary(null); setImportOpen(true); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(null); setSummary(null); setImporting(true);
    try {
      const res = await schoolApi.importFeeTypesPreview(orgId, selectedYearId, file);
      setPreview(res);
      if (res.total === 0) toast.info('No data rows found in the file');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not read the file'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const runImport = async () => {
    if (!preview) return;
    const valid = preview.rows.filter((r) => r.valid);
    if (valid.length === 0) { toast.error('No valid rows to import'); return; }
    setImporting(true);
    try {
      const body = {
        organization_id: orgId,
        rows: valid.map((r) => ({
          name: r.name, fee_group_id: r.fee_group_id, due_date: r.due_date,
          amount: r.amount, description: r.description, applicable_class_ids: r.applicable_class_ids,
        })),
      };
      const res = await schoolApi.importFeeTypes(body);
      setSummary(res); setPreview(null);
      toast.success(`Imported ${res.imported} fee type(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };
  const closeImport = () => { setImportOpen(false); setPreview(null); setSummary(null); };

  return (
    <div className="space-y-6" data-testid="fees-types-page">
      <PageHeader icon={SlidersHorizontal} title={t('nav.feesTypes', 'Fees Types')} subtitle="Individual fee line items"
        actions={(
          <div className="flex gap-2">
            {canEditTypes && <Button variant="outline" onClick={openImport} data-testid="fee-type-import-open"><Upload className="w-4 h-4 mr-1" /> Import from Excel</Button>}
            {canAdd && <Button onClick={openNew} disabled={!groups.length} data-testid="fee-type-new"><Plus className="w-4 h-4 mr-1" /> New Fee Type</Button>}
          </div>
        )} />
      {!groups.length && !loading && <Card className="p-3 text-sm text-amber-600">Create a Fees Group first.</Card>}
      <Card className="p-3">
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search fee types..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fee-type-search" />
        </div>
      </Card>
      {loading ? <LoadingState /> : rows.length === 0 ? <EmptyState icon={SlidersHorizontal} title="No fee types" /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="p-3">Name</th><th className="p-3">Group</th><th className="p-3">Applicable classes</th><th className="p-3">Due date</th><th className="p-3 text-right">Default amount</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-muted/40" data-testid={`fee-type-${r.id}`}>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3">{r.fee_group_name || '\u2014'}</td>
                  <td className="p-3" data-testid={`fee-type-classes-${r.id}`}>
                    {(r.applicable_class_names && r.applicable_class_names.length > 0) ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {r.applicable_class_names.slice(0, 4).map((n, i) => <Badge key={i} variant="outline" className="text-xs">{n}</Badge>)}
                        {r.applicable_class_names.length > 4 && <Badge variant="outline" className="text-xs">+{r.applicable_class_names.length - 4}</Badge>}
                      </div>
                    ) : <span className="text-muted-foreground text-xs">All classes</span>}
                  </td>
                  <td className="p-3">{r.due_date || '\u2014'}</td>
                  <td className="p-3 text-right">{(r.default_amount || 0).toLocaleString()}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canEditTypes && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`fee-type-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {canDeleteTypes && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r)} data-testid={`fee-type-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    {!canEditTypes && !canDeleteTypes && <span className="text-muted-foreground text-xs">{'\u2014'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent data-testid="fee-type-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? 'Edit' : 'New'} Fee Type</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Frais Octobre" data-testid="fee-type-name" /></div>
            <div><Label>Fees Group *</Label>
              <Select value={form.fee_group_id} onValueChange={(v) => setForm({ ...form, fee_group_id: v })}>
                <SelectTrigger data-testid="fee-type-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Applicable Classes</Label>
              <ClassMultiSelect classes={classes} value={form.applicable_class_ids} onChange={(ids) => setForm({ ...form, applicable_class_ids: ids })} />
              <p className="text-xs text-muted-foreground mt-1">If none selected, this fee type applies to all classes.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="fee-type-due" /></div>
              <div><Label>Default amount</Label><Input type="number" value={form.default_amount} onChange={(e) => setForm({ ...form, default_amount: e.target.value })} data-testid="fee-type-amount" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="fee-type-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) closeImport(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="fee-type-import-dialog">
          <DialogHeader><DialogTitle>Import Fee Types from Excel</DialogTitle></DialogHeader>

          {/* Step 1: upload */}
          {!summary && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Expected columns: <strong>Fee Name</strong>, <strong>Fees Group</strong>, <strong>Due Date</strong>, <strong>Amount</strong>, <strong>Description</strong>, <strong>Applicable Classes</strong> (comma-separated class names). Class names are matched against the currently selected academic year.
              </div>
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept=".xlsx" onChange={onFile} className="hidden" data-testid="fee-type-import-file" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="fee-type-import-choose">
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> {importing && !preview ? 'Reading...' : 'Choose .xlsx file'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: preview */}
          {preview && !summary && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <Badge className="bg-muted text-foreground">Sheet: {preview.sheet}</Badge>
                <Badge className="bg-muted text-foreground">Total rows: {preview.total}</Badge>
                <Badge className={STATUS_BADGE.ok} data-testid="import-valid-count">Valid: {preview.valid_count}</Badge>
                <Badge className={STATUS_BADGE.error}>Flagged: {preview.invalid_count}</Badge>
              </div>
              <div className="border rounded-md overflow-x-auto max-h-[46vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead className="sticky top-0 bg-background"><tr className="border-b text-left text-muted-foreground">
                    <th className="p-2">#</th><th className="p-2">Name</th><th className="p-2">Group</th><th className="p-2">Due</th>
                    <th className="p-2 text-right">Amount</th><th className="p-2">Classes</th><th className="p-2">Status</th><th className="p-2">Issues</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-b align-top" data-testid={`import-row-${r.row}`}>
                        <td className="p-2 text-muted-foreground">{r.row}</td>
                        <td className="p-2 font-medium">{r.name || '\u2014'}</td>
                        <td className="p-2">{r.group_name || '\u2014'}</td>
                        <td className="p-2">{r.due_date || r.due_date_raw || '\u2014'}</td>
                        <td className="p-2 text-right">{(r.amount || 0).toLocaleString()}</td>
                        <td className="p-2">{r.applicable_class_names?.length ? r.applicable_class_names.join(', ') : <span className="text-muted-foreground">All</span>}</td>
                        <td className="p-2">
                          <Badge className={STATUS_BADGE[r.status]} data-testid={`import-status-${r.row}`}>
                            {r.status === 'ok' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {r.status === 'duplicate' && <Copy className="w-3 h-3 mr-1" />}
                            {r.status === 'error' && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {r.status === 'ok' ? 'Ready' : r.status === 'duplicate' ? 'Duplicate' : 'Error'}
                          </Badge>
                        </td>
                        <td className="p-2 text-muted-foreground">{r.issues?.join('; ') || '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-muted-foreground">Only rows marked <strong>Ready</strong> will be imported. Flagged rows (duplicates / errors) are skipped.</div>
            </div>
          )}

          {/* Step 3: summary */}
          {summary && (
            <div className="space-y-3" data-testid="import-summary">
              <div className="flex gap-3">
                <div className="flex-1 border rounded-md p-4 bg-emerald-50">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="import-summary-imported">{summary.imported}</div>
                  <div className="text-sm text-emerald-700">Imported</div>
                </div>
                <div className="flex-1 border rounded-md p-4 bg-amber-50">
                  <div className="text-2xl font-bold text-amber-700" data-testid="import-summary-skipped">{summary.skipped}</div>
                  <div className="text-sm text-amber-700">Skipped</div>
                </div>
              </div>
              {summary.skipped_details?.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="px-3 py-2 text-xs font-semibold bg-muted">Skipped rows</div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {summary.skipped_details.map((s, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-medium">{s.name || '\u2014'}</td>
                            <td className="p-2 text-muted-foreground">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {summary ? (
              <Button onClick={closeImport} data-testid="import-done"><X className="w-4 h-4 mr-1" /> Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeImport}>Cancel</Button>
                {preview && (
                  <Button onClick={runImport} disabled={importing || preview.valid_count === 0} data-testid="import-confirm">
                    <Upload className="w-4 h-4 mr-1" /> {importing ? 'Importing...' : `Import ${preview.valid_count} valid row(s)`}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
